"use client";

import { useEffect, useState } from "react";
import { getBurns, formatTimeAgo, shortenAddress, type BurnRecord } from "@/lib/burn";
import { ExternalLink, Flame } from "lucide-react";

const SOLSCAN_URL = "https://solscan.io";

interface RecentBurnsProps {
  refreshTrigger?: number;
  maxDisplay?: number;
}

export function RecentBurns({ refreshTrigger = 0, maxDisplay = 10 }: RecentBurnsProps) {
  const [burns, setBurns] = useState<BurnRecord[]>([]);

  useEffect(() => {
    setBurns(getBurns().slice(0, maxDisplay));
  }, [refreshTrigger, maxDisplay]);

  if (burns.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-4 h-4 text-[#f9cd4f]" />
        <h2 className="text-base font-semibold text-white">Recent Burns</h2>
        <span className="text-xs text-[#666]">({burns.length})</span>
      </div>

      <div className="flex flex-col gap-2">
        {burns.map((burn) => (
          <div
            key={burn.signature}
            className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-xl border border-[#1a1a1a]"
          >
            {/* Token logo */}
            <div className="w-8 h-8 shrink-0">
              {burn.logoURI ? (
                <img
                  src={burn.logoURI}
                  alt={burn.tokenSymbol || "token"}
                  className="w-8 h-8 rounded-full"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#222] flex items-center justify-center text-[#666] text-sm font-semibold">
                  {(burn.tokenSymbol?.[0] || "?").toUpperCase()}
                </div>
              )}
            </div>

            {/* Burn info */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white">
                <span className="text-[#f9cd4f] font-semibold">{burn.amount}</span>{" "}
                <span className="text-[#888]">{burn.tokenSymbol || shortenAddress(burn.mintAddress, 4)}</span>
              </div>
              <div className="text-xs text-[#666]">{formatTimeAgo(burn.timestamp)}</div>
            </div>

            {/* Solscan link */}
            <a
              href={`${SOLSCAN_URL}/tx/${burn.signature}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#666] hover:text-[#f9cd4f] p-1 shrink-0"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
